import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { createLogger } from '@/lib/logger';
import { draftStore } from '@/lib/draftStore';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { Profile } from '@/types/database';
import { useProfileStore } from '@/stores/useProfileStore';
import { initHaptics } from '@/lib/haptics';
import * as Linking from 'expo-linking';
import { parseRecoveryUrl, parseUrlFragment } from '@/lib/linking';
import { useRouter } from 'expo-router';
import { AppState, AppStateStatus } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { debugStore } from '@/lib/debugStore';
import { setUser as setSentryUser, captureError } from '@/lib/errorReporting';
import { identifyUser } from '@/lib/analytics';
import { resetAll as resetTutorials } from '@/lib/tutorials';
import { registerForPushNotifications, unregisterPushToken } from '@/lib/pushNotifications';
import { revenueCatLogIn, revenueCatLogOut } from '@/lib/revenueCat';

const log = createLogger('AUTH');

/** Maximum time (ms) to wait for the initial session check before unblocking the UI. */
const INIT_TIMEOUT_MS = 10_000;

/** Margin (seconds) before token expiry at which we proactively refresh. */
const EXPIRY_MARGIN_S = 120;

interface AuthContextType {
  session: Session | null;
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  profileLoading: boolean;
  isPasswordRecoverySession: boolean;
  clearRecoverySession: () => void;
  signUp: (email: string, password: string) => Promise<{ data: any; error: any }>;
  signIn: (email: string, password: string) => Promise<{ ok: boolean; errorMessage?: string }>;
  signOut: () => Promise<void>;
  refreshProfile: (force?: boolean) => Promise<Profile | null>;
  setOnboardingResetCallback: (callback: () => void) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Returns true if the session's access token is expired or will expire
 * within EXPIRY_MARGIN_S seconds.
 */
function isSessionExpiredOrStale(session: Session | null): boolean {
  if (!session?.expires_at) return true;
  const nowSeconds = Math.floor(Date.now() / 1000);
  return session.expires_at - nowSeconds < EXPIRY_MARGIN_S;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const [isPasswordRecoverySession, setIsPasswordRecoverySession] = useState(false);
  const onboardingResetCallback = React.useRef<(() => void) | null>(null);
  const isHandlingSignOutRef = useRef(false);

  const fetchProfile = async (userId: string, force = false): Promise<Profile | null> => {
    if (!userId) return null;

    setProfileLoading(true);
    try {
      // Delegate to the Zustand profile store (single source of truth)
      const result = await useProfileStore.getState().fetchProfile(userId, force);
      // Keep local context state in sync for backward compat
      setProfile(result);
      return result;
    } finally {
      setProfileLoading(false);
    }
  };

  const handleAuthDeepLink = async (url: string) => {
    log.info('[AUTH] Handling auth deep link:', url);

    const { queryParams } = Linking.parse(url);
    const fragmentParams = parseUrlFragment(url);
    const allParams = { ...queryParams, ...fragmentParams };

    debugStore.setDeepLink(url, {
      queryParams,
      fragmentParams,
      allParams,
    });

    const type = (allParams.type as string) || (queryParams?.type as string);
    log.info('[AUTH] Link type:', type);

    if (type === 'recovery') {
      const recoveryData = parseRecoveryUrl(url);

      if (!recoveryData) {
        log.info('[AUTH] No recovery data found in URL');
        log.info('[AUTH] Query params:', queryParams);
        log.info('[AUTH] Fragment params:', fragmentParams);
        return;
      }

      try {
        if (recoveryData.format === 'session') {
          log.info('[AUTH] Setting session with access_token and refresh_token');
          const { error } = await supabase.auth.setSession({
            access_token: recoveryData.accessToken,
            refresh_token: recoveryData.refreshToken,
          });

          if (error) {
            log.error('[AUTH] setSession error:', error.message, error);
            throw error;
          }

          log.info('[AUTH] Session established successfully');
          setIsPasswordRecoverySession(true);
          router.push('/reset-password');
        } else if (recoveryData.format === 'otp') {
          log.info('[AUTH] Verifying OTP with token_hash');
          const { error } = await supabase.auth.verifyOtp({
            token_hash: recoveryData.tokenHash,
            type: 'recovery',
          });

          if (error) {
            log.error('[AUTH] verifyOtp error:', error.message, error);
            throw error;
          }

          log.info('[AUTH] OTP verified successfully');
          setIsPasswordRecoverySession(true);
          router.push('/reset-password');
        }
      } catch (error: any) {
        log.error('[AUTH] Failed to establish recovery session:', error.message, error);
        setIsPasswordRecoverySession(false);
      }
    } else if (type === 'signup' || type === 'email_confirmation') {
      log.info('[AUTH] Email confirmation link detected');
      try {
        if (allParams.token_hash) {
          const { error } = await supabase.auth.verifyOtp({
            token_hash: allParams.token_hash as string,
            type: 'email',
          });
          if (error) {
            log.error('[AUTH] Email confirmation error:', error.message, error);
          } else {
            log.info('[AUTH] Email confirmed successfully');
          }
        }
      } catch (error: any) {
        log.error('[AUTH] Email confirmation failed:', error.message, error);
      }
    } else if (allParams.access_token && allParams.refresh_token) {
      // OAuth callback (e.g. Google Sign-In) — tokens in URL fragment
      log.info('[AUTH] OAuth callback detected — setting session from tokens');
      try {
        const { error } = await supabase.auth.setSession({
          access_token: allParams.access_token as string,
          refresh_token: allParams.refresh_token as string,
        });
        if (error) {
          log.error('[AUTH] OAuth setSession error:', error.message);
        } else {
          log.info('[AUTH] OAuth session established — navigating to gate');
          router.replace('/gate' as any);
        }
      } catch (error: any) {
        log.error('[AUTH] OAuth session failed:', error.message);
      }
    } else {
      log.info('[AUTH] Unknown link type or no type specified');
    }
  };

  /**
   * Attempt to refresh the session if the access token is stale.
   * Returns the refreshed session or null if refresh failed (user should re-auth).
   */
  const tryRefreshSession = useCallback(async (current: Session | null): Promise<Session | null> => {
    if (!current) return null;
    if (!isSessionExpiredOrStale(current)) return current;

    log.info('[AUTH] Token stale/expired, attempting refresh');
    try {
      const { data, error } = await supabase.auth.refreshSession();
      if (error || !data.session) {
        log.warn('[AUTH] Token refresh failed, forcing sign-out:', error?.message);
        return null; // Caller should sign user out
      }
      log.info('[AUTH] Token refreshed successfully');
      return data.session;
    } catch (e: any) {
      log.error('[AUTH] Token refresh exception:', e.message);
      return null;
    }
  }, []);

  useEffect(() => {
    initHaptics();
    let initTimedOut = false;

    // Safety valve: if initial session check hangs, unblock UI
    const initTimer = setTimeout(() => {
      initTimedOut = true;
      setLoading(prev => {
        if (prev) {
          log.warn('[AUTH] Initial session check timed out after', INIT_TIMEOUT_MS, 'ms — unblocking UI');
          return false;
        }
        return prev;
      });
    }, INIT_TIMEOUT_MS);

    // Check for initial URL (app opened via deep link)
    Linking.getInitialURL().then(url => {
      if (url) {
        log.info('[AUTH] Initial URL:', url);
        handleAuthDeepLink(url);
      } else if (typeof window !== 'undefined' && window.location.hash) {
        // Web: expo-linking may miss hash fragments from OAuth redirects
        const fullUrl = window.location.href;
        log.info('[AUTH] Web hash fragment detected:', fullUrl);
        handleAuthDeepLink(fullUrl);
      }
    });

    // Listen for deep links while app is running
    const linkingSubscription = Linking.addEventListener('url', ({ url }) => {
      log.info('[AUTH] Deep link received:', url);
      handleAuthDeepLink(url);
    });

    // Initial session check with staleness detection
    supabase.auth.getSession().then(async ({ data: { session: initialSession } }) => {
      clearTimeout(initTimer);
      if (initTimedOut) {
        log.info('[AUTH] Init resolved after timeout — continuing with result');
      }

      log.info('[AUTH] Initial session check:', {
        hasSession: !!initialSession,
        userId: initialSession?.user?.id,
        expiresAt: initialSession?.expires_at,
      });

      let activeSession = initialSession;

      // If the stored session is stale, try to refresh it
      if (initialSession && isSessionExpiredOrStale(initialSession)) {
        activeSession = await tryRefreshSession(initialSession);
        if (!activeSession) {
          // Refresh failed — clear everything, user must re-authenticate
          log.warn('[AUTH] Stale session could not be refreshed — clearing');
          setSession(null);
          setUser(null);
          setProfile(null);
          setLoading(false);
          return;
        }
      }

      setSession(activeSession);
      setUser(activeSession?.user ?? null);
      if (activeSession?.user) {
        // fetchProfile sets profileLoading internally
        await fetchProfile(activeSession.user.id);
        // Register for push notifications (fire-and-forget)
        registerForPushNotifications(activeSession.user.id).catch(() => {});
        // Identify user in RevenueCat (fire-and-forget)
        revenueCatLogIn(activeSession.user.id).catch(() => {});
        // Report tamper detection flags if any
        try {
          const flags = (globalThis as any).__duelIntegrityFlags;
          if (flags && flags.length > 0) {
            const { reportIntegrityStatus } = await import('@/lib/tamperDetection');
            reportIntegrityStatus(activeSession.user.id, { isSecure: false, flags }).catch(() => {});
            delete (globalThis as any).__duelIntegrityFlags;
          }
        } catch {}
      }
      setLoading(false);
    }).catch((err: any) => {
      clearTimeout(initTimer);
      log.error('[AUTH] getSession failed:', err.message);
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      (async () => {
        log.info('[AUTH] Auth state change:', {
          event,
          hasSession: !!session,
          userId: session?.user?.id,
        });

        debugStore.setAuthEvent(event);

        if (event === 'PASSWORD_RECOVERY') {
          log.info('[AUTH] PASSWORD_RECOVERY event detected');
          setIsPasswordRecoverySession(true);
          router.push('/reset-password');
        }

        if (event === 'TOKEN_REFRESHED') {
          log.info('[AUTH] Token refreshed via Supabase auto-refresh', {
            expiresAt: session?.expires_at,
          });
          setSession(session);
          setUser(session?.user ?? null);
          return;
        }

        if (event === 'SIGNED_OUT') {
          if (isHandlingSignOutRef.current) {
            log.info('[AUTH] Sign out already being handled, ignoring duplicate event');
            return;
          }
          isHandlingSignOutRef.current = true;
          try {
            log.info('[AUTH] Sign out detected, clearing state');
            setSession(null);
            setUser(null);
            setProfile(null);
            setIsPasswordRecoverySession(false);
            router.replace('/(auth)/welcome');
            log.info('[AUTH] Sign out complete');
          } finally {
            isHandlingSignOutRef.current = false;
          }
          return;
        }

        setSession(session);
        setUser(session?.user ?? null);
        if (session?.user) {
          await fetchProfile(session.user.id);
        } else {
          setProfile(null);
        }

        if (session && event === 'SIGNED_IN') {
          // Register push token for the newly signed-in user
          registerForPushNotifications(session.user.id).catch(() => {});
          // Identify user in RevenueCat
          revenueCatLogIn(session.user.id).catch(() => {});
          // Report tamper detection flags if any
          try {
            const flags = (globalThis as any).__duelIntegrityFlags;
            if (flags && flags.length > 0) {
              const { reportIntegrityStatus } = await import('@/lib/tamperDetection');
              reportIntegrityStatus(session.user.id, { isSecure: false, flags }).catch(() => {});
              delete (globalThis as any).__duelIntegrityFlags;
            }
          } catch {}
          if (__DEV__) {
            log.info('[AUTH] SIGNED_IN event - navigating to /gate');
          }
          router.replace('/gate');
        }
      })();
    });

    // App-state listener: refresh session + profile when app comes to foreground
    // Debounced to 10s to prevent stack overflow when app rapidly enters/exits background
    let lastForegroundRefresh = 0;
    const FOREGROUND_REFRESH_DEBOUNCE_MS = 10_000;
    const appStateSubscription = AppState.addEventListener('change', (state: AppStateStatus) => {
      if (state === 'active') {
        const now = Date.now();
        if (now - lastForegroundRefresh < FOREGROUND_REFRESH_DEBOUNCE_MS) return;
        lastForegroundRefresh = now;
        supabase.auth.getSession().then(async ({ data: { session: current } }) => {
          if (current && isSessionExpiredOrStale(current)) {
            log.info('[AUTH] App foregrounded with stale token — refreshing');
            const refreshed = await tryRefreshSession(current);
            if (!refreshed) {
              log.warn('[AUTH] Foreground refresh failed — signing out');
              await supabase.auth.signOut({ scope: 'local' });
              return;
            }
          }
          // Force-refresh profile on foreground so subscription_tier
          // reflects any purchases made in a prior session (e.g. RevenueCat sync)
          if (current?.user?.id) {
            fetchProfile(current.user.id, true).catch(() => {});
          }
        });
      }
    });

    return () => {
      clearTimeout(initTimer);
      subscription.unsubscribe();
      linkingSubscription.remove();
      appStateSubscription.remove();
    };
  }, []);

  const signUp = async (email: string, password: string) => {
    // Reset tutorials so new accounts get the full walkthrough
    await resetTutorials();
    try {
      const { data, error } = await supabase.auth.signUp({ email, password });
      return { data, error };
    } catch (err: any) {
      const msg = err?.message || '';
      const isServiceError = msg.includes('Unexpected character') || msg.includes('JSON') || msg.includes('<!');
      const friendlyError = { message: isServiceError ? 'Unable to connect to DUEL servers. Please try again in a moment.' : msg };
      return { data: null, error: friendlyError as any };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const normalizedEmail = email.trim().toLowerCase();

      log.info('[SIGNIN] Attempting sign in:', {
        email: normalizedEmail,
      });

      const { data, error } = await supabase.auth.signInWithPassword({
        email: normalizedEmail,
        password,
      });

      if (error) {
        log.info('[SIGNIN] Error:', {
          message: error.message,
          status: error.status,
        });
        captureError(error, { context: 'signIn', email: normalizedEmail });
        return { ok: false, errorMessage: error.message };
      }

      log.info('[SIGNIN] Success:', {
        userId: data.user?.id,
        hasSession: !!data.session,
      });

      return { ok: true };
    } catch (err: any) {
      log.info('[SIGNIN] Exception:', err);
      captureError(err, { context: 'signIn' });
      // Detect HTML response (Supabase outage / project restart returning error page)
      const msg = err?.message || '';
      const isServiceError = msg.includes('Unexpected character') || msg.includes('JSON') || msg.includes('<!');
      return {
        ok: false,
        errorMessage: isServiceError
          ? 'Unable to connect to DUEL servers. Please try again in a moment.'
          : msg || 'An unexpected error occurred',
      };
    }
  };

  const signOut = async () => {
    // Mark that we are driving sign-out so the onAuthStateChange handler
    // doesn't race with us (duplicate state clear + navigate).
    isHandlingSignOutRef.current = true;
    try {
      log.info('[AUTH] signOut: started');

      // Unregister push token before clearing session
      if (user?.id) {
        await unregisterPushToken(user.id).catch(() => {});
      }
      // Log out of RevenueCat
      revenueCatLogOut().catch(() => {});

      if (onboardingResetCallback.current) {
        onboardingResetCallback.current();
      }

      try {
        await supabase.auth.signOut();
        log.info('[AUTH] signOut: server sign-out succeeded');
      } catch (networkErr: any) {
        // Server-side sign-out failed (network issue, timeout, etc.)
        // Fall back to local-only sign-out so the user isn't stuck.
        log.warn('[AUTH] signOut: server sign-out failed, falling back to local:', networkErr.message);
        try {
          await supabase.auth.signOut({ scope: 'local' });
        } catch (localErr: any) {
          log.error('[AUTH] signOut: even local sign-out failed:', localErr.message);
        }
      }
    } finally {
      setSession(null);
      setUser(null);
      setProfile(null);
      useProfileStore.getState().clear();
      setIsPasswordRecoverySession(false);
      void draftStore.clearAll();

      // Clear onboarding checkpoint so new accounts start fresh
      try {
        await AsyncStorage.removeItem('onboarding:lastStep');
        await AsyncStorage.removeItem('onboarding:walkthrough_complete');
      } catch (e) {
        log.warn('[AUTH] signOut: failed to clear onboarding state:', e);
      }

      // Clear any lingering auth tokens from browser storage (prevents auto-restore)
      if (typeof window !== 'undefined') {
        try {
          const storageKeys = Object.keys(window.localStorage).filter(
            k => k.startsWith('sb-') && (k.endsWith('-auth-token') || k.endsWith('-auth-token-code-verifier'))
          );
          storageKeys.forEach(k => window.localStorage.removeItem(k));
          log.info('[AUTH] signOut: cleared browser auth storage keys:', storageKeys.length);
        } catch (e) {
          log.warn('[AUTH] signOut: failed to clear browser storage:', e);
        }
      }

      router.replace('/(auth)/welcome');
      log.info('[AUTH] signOut: complete');
      // Reset the flag after a tick so any late SIGNED_OUT event is still suppressed
      setTimeout(() => { isHandlingSignOutRef.current = false; }, 500);
    }
  };

  const setOnboardingResetCallback = (callback: () => void) => {
    onboardingResetCallback.current = callback;
  };

  const refreshProfile = async (force = false) => {
    if (!user?.id) return null;
    return await fetchProfile(user.id, force);
  };

  const clearRecoverySession = () => {
    setIsPasswordRecoverySession(false);
  };

  const value = {
    session,
    user,
    profile,
    loading,
    profileLoading,
    isPasswordRecoverySession,
    clearRecoverySession,
    signUp,
    signIn,
    signOut,
    refreshProfile,
    setOnboardingResetCallback,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
